import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircle, Shield, Clock, DollarSign, ArrowRight, Star } from 'lucide-react';

const BrandLandingPage = () => {
  const location = useLocation();
  
  // Extract brand from pathname
  const pathname = location.pathname;
  let brand = 'amazon';
  if (pathname.includes('apple')) brand = 'apple';
  else if (pathname.includes('walmart')) brand = 'walmart';
  else if (pathname.includes('target')) brand = 'target';
  else if (pathname.includes('bestbuy')) brand = 'bestbuy';
  else if (pathname.includes('homedepot')) brand = 'homedepot';
  else if (pathname.includes('lowes')) brand = 'lowes';
  else if (pathname.includes('macys')) brand = 'macys';
  else if (pathname.includes('lululemon')) brand = 'lululemon';
  else if (pathname.includes('dicks')) brand = 'dicks';
  else if (pathname.includes('sephora')) brand = 'sephora';
  else if (pathname.includes('starbucks')) brand = 'starbucks';
  else if (pathname.includes('disney')) brand = 'disney';
  else if (pathname.includes('nike')) brand = 'nike';
  else if (pathname.includes('ebay')) brand = 'ebay';
  else if (pathname.includes('steam')) brand = 'steam';
  else if (pathname.includes('razergold')) brand = 'razergold';
  else if (pathname.includes('ulta')) brand = 'ulta';
  else if (pathname.includes('amex')) brand = 'amex';
  else if (pathname.includes('visa')) brand = 'visa';
  
  // Brand-specific configurations
  const brandConfig = {
    amazon: {
      name: "Amazon",
      logo: "🛒",
      color: "orange",
      rate: "up to 93%",
      description: "Turn your unused Amazon gift cards into instant cash. We offer the highest rates and same-day payouts.",
      keywords: "sell Amazon gift cards, Amazon card exchange, trade Amazon cards for cash",
      faqs: [
        {
          question: "How do I sell my Amazon gift card on Cashifygcmart?",
          answer: "Simply upload your card images or enter the code manually, our team verifies authenticity within hours, and you get paid securely the same day via your preferred method."
        },
        {
          question: "What rate do you offer for Amazon gift cards?",
          answer: "We offer up to 93% of face value for Amazon gift cards, depending on the card condition and verification. Use our Rate Calculator for an instant quote."
        },
        {
          question: "How fast will I receive payment for my Amazon card?",
          answer: "Most Amazon card payouts are processed within 6 hours after verification. We support PayPal, Zelle, Cash App, Bitcoin, and Chime for instant transfers."
        }
      ]
    },
    apple: {
      name: "Apple",
      logo: "🍎",
      color: "gray",
      rate: "up to 90%",
      description: "Sell your Apple gift cards and iTunes cards for cash instantly. Trusted by thousands, with secure verification and fast payouts.",
      keywords: "sell Apple gift cards, iTunes card exchange, trade Apple cards for cash",
      faqs: [
        {
          question: "Do you buy Apple and iTunes gift cards?",
          answer: "Yes! We buy both Apple Store gift cards and iTunes/App Store cards. Simply submit your card details and we'll verify and pay you the same day."
        },
        {
          question: "What's the payout rate for Apple gift cards?",
          answer: "Apple gift cards receive up to 90% of face value depending on verification. Check our Rate Calculator for your specific card value."
        },
        {
          question: "Is it safe to sell my Apple gift card online?",
          answer: "Absolutely. We use bank-level encryption, manual validation, and AI fraud checks to protect every transaction. Your data is auto-deleted after 72 hours."
        }
      ]
    },
    walmart: {
      name: "Walmart",
      logo: "🏪",
      color: "blue",
      rate: "up to 91%",
      description: "Get instant cash for your Walmart gift cards. Fast verification, competitive rates, and same-day payouts guaranteed.",
      keywords: "sell Walmart gift cards, Walmart card exchange, trade Walmart cards for cash",
      faqs: [
        {
          question: "How much can I get for my Walmart gift card?",
          answer: "We pay up to 91% of your Walmart card's face value. The exact rate depends on card condition and balance verification. Get an instant quote with our Rate Calculator."
        },
        {
          question: "Do I need the receipt to sell my Walmart card?",
          answer: "No, receipts are not required but they can help increase your payout rate. We accept cards with or without receipts."
        },
        {
          question: "How do I verify my Walmart gift card balance?",
          answer: "You can check your balance at Walmart.com/giftcardbalance or on our platform. We'll verify the balance during our review process before payout."
        }
      ]
    },
    target: {
      name: "Target",
      logo: "🎯",
      color: "red",
      rate: "up to 92%",
      description: "Sell Target gift cards for instant cash. We offer top rates, secure processing, and same-day payouts to your preferred method.",
      keywords: "sell Target gift cards, Target card exchange, trade Target cards for cash",
      faqs: [
        {
          question: "What's your rate for Target gift cards?",
          answer: "Target gift cards receive up to 92% of face value. Rates may vary based on card condition and balance. Use our Rate Calculator for an accurate quote."
        },
        {
          question: "Can I sell a partially used Target gift card?",
          answer: "Yes! We accept partially used Target cards as long as the remaining balance is at least $50. You'll receive payment based on the current balance."
        },
        {
          question: "How long does Target gift card verification take?",
          answer: "Target cards are typically verified within 2-6 hours. Most payouts are processed the same business day after successful verification."
        }
      ]
    },
    bestbuy: {
      name: "Best Buy",
      logo: "🔌",
      color: "yellow",
      rate: "up to 89%",
      description: "Trade your Best Buy gift cards for cash today. Fast verification, competitive rates, and secure same-day payouts.",
      keywords: "sell Best Buy gift cards, Best Buy card exchange, trade Best Buy cards for cash",
      faqs: [
        {
          question: "Do you accept Best Buy gift cards?",
          answer: "Yes! We buy Best Buy gift cards and offer up to 89% of face value. Submit your card details and get paid the same day after verification."
        },
        {
          question: "What if my Best Buy card has an unusual balance?",
          answer: "No problem! We accept Best Buy cards with any balance amount (minimum $50). Our verification team will confirm the exact balance before payout."
        },
        {
          question: "Are Best Buy e-gift cards accepted?",
          answer: "Yes, we accept both physical and digital Best Buy gift cards. For e-gift cards, simply provide the card number and PIN from your email."
        }
      ]
    },
    homedepot: {
      name: "Home Depot",
      logo: "🏗️",
      color: "orange",
      rate: "up to 88%",
      description: "Convert Home Depot gift cards into cash quickly. Trusted resale platform with fast verification and secure payouts.",
      keywords: "sell Home Depot gift cards, Home Depot card exchange, trade Home Depot cards for cash",
      faqs: [
        {
          question: "Can I sell partially used Home Depot gift cards?",
          answer: "Yes, our verification system checks the remaining balance before payout. Minimum balance required is $50."
        },
        {
          question: "How fast is payment for Home Depot gift cards?",
          answer: "Most Home Depot cards are paid within a few hours after verification. We support multiple payout methods for instant access."
        },
        {
          question: "Do you accept Home Depot eGift cards?",
          answer: "Yes, we accept both physical cards and digital eGift cards after manual authentication."
        }
      ]
    },
    lowes: {
      name: "Lowe's",
      logo: "🔧",
      color: "blue",
      rate: "up to 87%",
      description: "Get instant cash for unused Lowe's cards. Verified resale made easy with competitive rates and same-day payouts.",
      keywords: "sell Lowe's gift cards, Lowe's card exchange, trade Lowe's cards for cash",
      faqs: [
        {
          question: "What rate do you offer for Lowe's gift cards?",
          answer: "We offer up to 87% of face value for Lowe's cards depending on condition and verification. Use our Rate Calculator for an instant quote."
        },
        {
          question: "How long does Lowe's card verification take?",
          answer: "Verification typically takes 2-6 hours. Most payouts are processed the same business day."
        },
        {
          question: "Do I need a receipt to sell my Lowe's card?",
          answer: "Receipts are not required but can help increase your payout rate by 2-5%."
        }
      ]
    },
    macys: {
      name: "Macy's",
      logo: "👗",
      color: "red",
      rate: "up to 86%",
      description: "Turn Macy's gift cards into real cash with quick verification and secure payments. Fashion cards accepted daily.",
      keywords: "sell Macy's gift cards, Macy's card exchange, trade Macy's cards for cash",
      faqs: [
        {
          question: "Do you buy Macy's gift cards?",
          answer: "Yes! We buy Macy's gift cards and offer up to 86% of face value. Submit your card and get paid the same day."
        },
        {
          question: "Can I sell Macy's gift cards without a receipt?",
          answer: "Yes, receipts are optional. However, having a receipt may increase your payout rate."
        },
        {
          question: "How fast will I receive payment?",
          answer: "Most Macy's cards are verified and paid within 4-8 hours after submission."
        }
      ]
    },
    lululemon: {
      name: "Lululemon",
      logo: "🧘",
      color: "red",
      rate: "up to 85%",
      description: "Sell Lululemon gift cards for instant cash. Trusted secure platform for fashion and fitness cards with fast payouts.",
      keywords: "sell Lululemon gift cards, Lululemon card exchange, trade Lululemon cards for cash",
      faqs: [
        {
          question: "Do you buy Lululemon eGift cards?",
          answer: "Yes, both physical and eGift cards are accepted after manual verification."
        },
        {
          question: "What's the payout rate for Lululemon cards?",
          answer: "Lululemon gift cards receive up to 85% of face value. Check our Rate Calculator for your specific amount."
        },
        {
          question: "How long does Lululemon gift card payout take?",
          answer: "Usually within the same day after authenticity confirmation."
        }
      ]
    },
    dicks: {
      name: "Dick's Sporting Goods",
      logo: "⚽",
      color: "green",
      rate: "up to 84%",
      description: "Instantly cash out your Dick's Sporting Goods gift cards with verified trades. Secure processing and fast payouts guaranteed.",
      keywords: "sell Dick's Sporting Goods gift cards, Dick's card exchange, trade sporting goods cards for cash",
      faqs: [
        {
          question: "Do you accept Dick's Sporting Goods gift cards?",
          answer: "Yes! We buy Dick's Sporting Goods cards and offer up to 84% of face value. Fast verification and same-day payment."
        },
        {
          question: "Can I sell partially used Dick's cards?",
          answer: "Yes, we accept partially used cards with a minimum balance of $50."
        },
        {
          question: "How fast is the verification process?",
          answer: "Dick's cards are typically verified within 2-6 hours with same-day payouts."
        }
      ]
    },
    sephora: {
      name: "Sephora",
      logo: "💄",
      color: "pink",
      rate: "up to 86%",
      description: "Exchange Sephora gift cards for fast cash and secure payouts. Beauty cards welcome with trusted verification.",
      keywords: "sell Sephora gift cards, Sephora card exchange, trade Sephora cards for cash",
      faqs: [
        {
          question: "Do you accept Sephora eGift cards?",
          answer: "Yes, both eGift and physical cards are accepted after manual review."
        },
        {
          question: "What's your rate for Sephora gift cards?",
          answer: "Sephora cards receive up to 86% of face value depending on verification. Use our Rate Calculator for an instant quote."
        },
        {
          question: "How long does Sephora card verification take?",
          answer: "Most verifications complete within a few hours with same-day payouts."
        }
      ]
    },
    starbucks: {
      name: "Starbucks",
      logo: "☕",
      color: "green",
      rate: "up to 85%",
      description: "Trade your Starbucks cards for cash instantly. AI-verified safety and same-day payment to your preferred method.",
      keywords: "sell Starbucks gift cards, Starbucks card exchange, trade Starbucks cards for cash",
      faqs: [
        {
          question: "Do you buy Starbucks gift cards?",
          answer: "Yes! We buy Starbucks cards and offer up to 85% of face value. Submit and get paid the same day."
        },
        {
          question: "Can I sell my Starbucks Rewards card?",
          answer: "We accept Starbucks gift cards. For Rewards cards, please contact our support team for assistance."
        },
        {
          question: "How fast will I receive payment?",
          answer: "Most Starbucks cards are verified and paid within 2-6 hours."
        }
      ]
    },
    disney: {
      name: "Disney",
      logo: "🏰",
      color: "blue",
      rate: "up to 84%",
      description: "Sell Disney gift cards securely and get paid instantly. Family entertainment cards accepted with fast verification.",
      keywords: "sell Disney gift cards, Disney card exchange, trade Disney cards for cash",
      faqs: [
        {
          question: "Do you accept Disney gift cards?",
          answer: "Yes! We buy Disney gift cards and offer up to 84% of face value. Perfect for unused vacation cards."
        },
        {
          question: "Can I sell partially used Disney gift cards?",
          answer: "Yes, balance verification ensures correct payout on partially used cards (minimum $50 balance)."
        },
        {
          question: "How fast do I get paid for Disney cards?",
          answer: "Usually within the same day after validation. Multiple payout options available."
        }
      ]
    },
    nike: {
      name: "Nike",
      logo: "👟",
      color: "orange",
      rate: "up to 85%",
      description: "Turn Nike gift cards into instant cash with verified processing. Sports brand cards accepted with competitive rates.",
      keywords: "sell Nike gift cards, Nike card exchange, trade Nike cards for cash",
      faqs: [
        {
          question: "What rate do you offer for Nike gift cards?",
          answer: "Nike cards receive up to 85% of face value. Check our Rate Calculator for your specific amount."
        },
        {
          question: "Do you accept Nike eGift cards?",
          answer: "Yes, we accept both physical and digital Nike gift cards after verification."
        },
        {
          question: "How long does Nike card payout take?",
          answer: "Most Nike cards are verified and paid within 4-8 hours."
        }
      ]
    },
    ebay: {
      name: "eBay",
      logo: "🛒",
      color: "red",
      rate: "up to 83%",
      description: "Sell eBay gift cards instantly and avoid common resale mistakes. Secure verification and fast payouts guaranteed.",
      keywords: "sell eBay gift cards, eBay card exchange, trade eBay cards for cash",
      faqs: [
        {
          question: "Do you buy eBay gift cards?",
          answer: "Yes! We buy eBay cards and offer up to 83% of face value. Submit your card for same-day payment."
        },
        {
          question: "Can I sell multiple eBay gift cards at once?",
          answer: "Yes, bulk submissions are supported and processed individually for safety."
        },
        {
          question: "What's the minimum value for eBay cards?",
          answer: "Minimum accepted value is usually $50 per card."
        }
      ]
    },
    steam: {
      name: "Steam",
      logo: "🎮",
      color: "blue",
      rate: "up to 82%",
      description: "Cash out Steam gift cards safely. Gaming credits to instant payouts made simple with secure verification.",
      keywords: "sell Steam gift cards, Steam card exchange, trade Steam cards for cash",
      faqs: [
        {
          question: "Do you buy Steam digital codes?",
          answer: "Yes, we accept both digital codes and physical Steam gift cards after verification."
        },
        {
          question: "What's your rate for Steam cards?",
          answer: "Steam cards receive up to 82% of face value. Use our Rate Calculator for an instant quote."
        },
        {
          question: "How long does Steam card payout take?",
          answer: "Most Steam cards are paid within a few hours once verified."
        }
      ]
    },
    razergold: {
      name: "Razer Gold",
      logo: "⚡",
      color: "green",
      rate: "up to 80%",
      description: "Sell Razer Gold cards for instant cash with manual verification and secure payout. Gaming credit resale made easy.",
      keywords: "sell Razer Gold gift cards, Razer Gold card exchange, trade gaming cards for cash",
      faqs: [
        {
          question: "Do you accept Razer Gold gift cards?",
          answer: "Yes! We buy Razer Gold cards and offer up to 80% of face value. Fast verification for gaming credits."
        },
        {
          question: "Can I sell Razer Gold PIN codes?",
          answer: "Yes, we accept PIN codes after manual verification to ensure authenticity."
        },
        {
          question: "How fast is payment for Razer Gold?",
          answer: "Most Razer Gold cards are verified and paid within 4-8 hours."
        }
      ]
    },
    ulta: {
      name: "Ulta Beauty",
      logo: "💅",
      color: "pink",
      rate: "up to 85%",
      description: "Convert Ulta Beauty cards into cash. Trusted resale and secure same-day payouts for beauty gift cards.",
      keywords: "sell Ulta gift cards, Ulta Beauty card exchange, trade Ulta cards for cash",
      faqs: [
        {
          question: "Can I sell Ulta Beauty eGift cards?",
          answer: "Yes, both eGift and physical Ulta Beauty cards are accepted."
        },
        {
          question: "What rate do you offer for Ulta cards?",
          answer: "Ulta cards receive up to 85% of face value. Check our Rate Calculator for your specific amount."
        },
        {
          question: "How quickly will I get paid?",
          answer: "Typically within 6-12 hours of verification."
        }
      ]
    },
    amex: {
      name: "American Express",
      logo: "💳",
      color: "blue",
      rate: "up to 88%",
      description: "Get cash for your American Express gift cards through secure AI-verified processing. Prepaid cards welcome.",
      keywords: "sell American Express gift cards, Amex card exchange, trade Amex cards for cash",
      faqs: [
        {
          question: "Do you buy American Express gift cards?",
          answer: "Yes! We buy Amex gift cards and offer up to 88% of face value. Secure processing and fast payouts."
        },
        {
          question: "Can I sell Amex prepaid cards?",
          answer: "Yes, prepaid Amex cards are accepted after balance and authenticity verification."
        },
        {
          question: "How long does Amex card verification take?",
          answer: "Most Amex cards are verified and paid within 4-8 hours."
        }
      ]
    },
    visa: {
      name: "Visa",
      logo: "💰",
      color: "blue",
      rate: "up to 89%",
      description: "Trade Visa gift cards for cash instantly and safely. Universal prepaid cards accepted with fast verification.",
      keywords: "sell Visa gift cards, Visa card exchange, trade Visa prepaid cards for cash",
      faqs: [
        {
          question: "Do you accept prepaid Visa cards?",
          answer: "Yes, prepaid Visa cards are accepted if balance and authenticity are verified."
        },
        {
          question: "What's your rate for Visa gift cards?",
          answer: "Visa cards receive up to 89% of face value. Use our Rate Calculator for an instant quote."
        },
        {
          question: "Is the payout same-day?",
          answer: "Yes, verified Visa cards are usually paid within hours via your preferred method."
        }
      ]
    }
  };

  const config = brandConfig[brand?.toLowerCase()] || brandConfig.amazon;
  
  // Generate FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": config.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  
  // Generate breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://cashifygcmart.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": `Sell ${config.name} Gift Cards`,
        "item": `https://cashifygcmart.com/sell-${brand?.toLowerCase()}-gift-cards`
      }
    ]
  };

  const colorClasses = {
    orange: "from-orange-500 to-orange-600",
    gray: "from-gray-700 to-gray-900",
    blue: "from-blue-500 to-blue-600",
    red: "from-red-500 to-red-600",
    yellow: "from-yellow-500 to-yellow-600",
    pink: "from-pink-500 to-pink-600",
    green: "from-green-500 to-green-600"
  };

  return (
    <>
      <Helmet>
        <title>Sell {config.name} Gift Cards Instantly for Cash | Cashifygcmart</title>
        <meta 
          name="description" 
          content={`${config.description} Get ${config.rate} cash value. Verified manually • Fast payouts • Trusted by thousands.`}
        />
        <meta name="keywords" content={config.keywords} />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className={`bg-gradient-to-r ${colorClasses[config.color]} text-white py-16`}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="text-6xl mb-4">{config.logo}</div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Sell {config.name} Gift Cards Instantly
              </h1>
              <p className="text-xl md:text-2xl mb-8 opacity-90">
                Get {config.rate} cash • Same-day payouts • Verified manually
              </p>
              <Link 
                to="/form-submission"
                className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition shadow-lg"
              >
                Sell Your Card Now <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                <span className="text-sm font-semibold">Verified Manually</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-semibold">Same-Day Payout</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                <span className="text-sm font-semibold">800,000+ Customers</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <span className="text-sm font-semibold">Best Rates</span>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            How to Sell Your {config.name} Gift Card
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-md text-center">
              <div className="bg-pink-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-pink-600">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Submit Card Details</h3>
              <p className="text-gray-600">
                Upload card images or enter code manually via our secure form
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">We Verify</h3>
              <p className="text-gray-600">
                Our team validates authenticity and balance within hours
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md text-center">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Get Paid Instantly</h3>
              <p className="text-gray-600">
                Receive cash via PayPal, Zelle, Cash App, Bitcoin, or Chime
              </p>
            </div>
          </div>
        </div>

        {/* FAQs Section */}
        <div className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {config.faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                    {faq.question}
                  </h3>
                  <p className="text-gray-700 pl-7">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className={`bg-gradient-to-r ${colorClasses[config.color]} text-white py-16`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Sell Your {config.name} Gift Card?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join 800,000+ satisfied customers who trust Cashifygcmart
            </p>
            <Link 
              to="/form-submission"
              className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition shadow-lg"
            >
              Start Selling Now <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default BrandLandingPage;
